import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getAdminSession, getAdminSessions, getCoachSessions, refreshSessionZoomLink } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

interface RefreshSessionZoomLinkProps {
  session: AdminSession;
  trigger: ReactNode;
}

export const RefreshSessionZoomLink = ({ session, trigger }: RefreshSessionZoomLinkProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => refreshSessionZoomLink(session.id),
  });

  const onSubmit = () => {
    return mutateAsync()
      .then(() => {
        toast({
          title: 'Zoom link updated',
          description: `The session's zoom links for both host and attendees have been updated`,
        });
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getAdminSession(session.id));
        queryClient.invalidateQueries(getCoachSessions());
        setOpen(false);
      })
      .catch((e: unknown) => {
        toast({
          title: 'Failed to refresh Zoom link',
          variant: 'destructive',
          description:
            airServerErrorFrom(e)?.userFacingMessage || 'Failed to refresh Zoom link. Please try again later.',
        });
      });
  };

  const errors: string[] = [];
  if (!session.coach) {
    errors.push(
      `No teacher is assigned to this session. Please assign a teacher first before refreshing the session's zoom link.`,
    );
  } else if (!session.coach.coachProfile.zoomPersonalMeetingId) {
    errors.push(
      `No zoom personal meeting id found for teacher: ${session.coach.id}. Please make sure the assigned teacher ${session.coach.fullNameOrEmail} has activated the Zoom account first.`,
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refresh Session Zoom Link</AlertDialogTitle>
          <AlertDialogDescription>
            {errors.length || !session.coach ? (
              <ul>
                {errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            ) : (
              <div>
                Every hour, the application will auto-refresh and populate upcoming sessions' Zoom host url. Use this
                feature to refresh the session's zoom link manually for testing or if the session's host url was not
                auto-populated. Confirm you want to refresh the session's Zoom link?
                <p className="font-bold">
                  Note: The host url will always be the session's assigned teacher's Zoom host url, even if there is a
                  substitution teacher.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={errors.length > 0}>
            <Button
              type="button"
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Submit
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
